/** Inline-SVG charts: 12-month trend, category bars, per-sub sparkline. */

import type { MonthBucket, Subscription } from '../lib/types';
import { el, svg } from './dom';
import type { Formatters } from './format';

export function renderTrend(container: HTMLElement, series: MonthBucket[]): void {
  const W = 560;
  const H = 180;
  const pad = { l: 8, r: 8, t: 24, b: 22 };
  const max = Math.max(...series.map((s) => s.total), 1);
  const bw = (W - pad.l - pad.r) / series.length;

  const chart = svg('svg', {
    viewBox: `0 0 ${W} ${H}`,
    preserveAspectRatio: 'xMidYMid meet',
    role: 'img',
    'aria-label': 'Recurring spend per month',
  });

  series.forEach((s, i) => {
    const h = (H - pad.t - pad.b) * (s.total / max);
    const x = pad.l + i * bw + bw * 0.15;
    const y = H - pad.b - h;
    const cx = pad.l + i * bw + bw / 2;
    chart.append(
      svg('rect', {
        x: x.toFixed(1),
        y: y.toFixed(1),
        width: (bw * 0.7).toFixed(1),
        height: Math.max(h, 1).toFixed(1),
        rx: '4',
        class: 'bar',
      }),
      svg('text', { x: cx.toFixed(1), y: String(H - 6), class: 'axis' }, s.label),
    );
    if (s.total > 0) {
      chart.append(
        svg('text', { x: cx.toFixed(1), y: (y - 5).toFixed(1), class: 'bar-val' }, String(Math.round(s.total))),
      );
    }
  });

  container.replaceChildren(chart);
}

export function renderCategories(container: HTMLElement, active: Subscription[], fmt: Formatters): void {
  const byCat = new Map<string, number>();
  for (const s of active) byCat.set(s.category, (byCat.get(s.category) ?? 0) + s.monthlyCost);
  const cats = [...byCat.entries()].sort((a, b) => b[1] - a[1]);
  const max = Math.max(...cats.map((c) => c[1]), 1);

  container.replaceChildren(
    ...cats.map(([cat, total]) => {
      const bar = el('div', { class: 'cat-bar' });
      bar.style.width = `${((total / max) * 100).toFixed(1)}%`;
      return el(
        'div',
        { class: 'cat-row' },
        el('div', { class: 'cat-name' }, cat),
        el('div', { class: 'cat-bar-track' }, bar),
        el('div', { class: 'cat-total' }, `${fmt.money(total)}/mo`),
      );
    }),
  );
}

export function sparkline(sub: Subscription): SVGElement | null {
  const txns = sub.txns.slice(-14);
  if (txns.length < 2) return null;
  const W = 90;
  const H = 26;
  const amounts = txns.map((t) => t.amount);
  const max = Math.max(...amounts);
  const min = Math.min(...amounts);
  const range = Math.max(max - min, max * 0.15, 0.01);
  const pts = txns.map((t, i) => {
    const x = (i / (txns.length - 1)) * (W - 4) + 2;
    const y = H - 3 - ((t.amount - min) / range) * (H - 8);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const rising = txns[txns.length - 1]!.amount > txns[0]!.amount * 1.05;
  return svg(
    'svg',
    { viewBox: `0 0 ${W} ${H}`, class: `spark ${rising ? 'spark-up' : ''}`, 'aria-hidden': 'true' },
    svg('polyline', { points: pts.join(' '), fill: 'none' }),
  );
}
